import type { ReactNode } from "react";
import { cx } from "./cx.js";
import { Icon } from "./Icon.jsx";
import type { Tone } from "./StatTile.jsx";

const TONE: Record<Tone, { cls: string; icon: string }> = {
  neutral: { cls: "bg-subtle text-ink-muted", icon: "radio_button_unchecked" },
  ok: { cls: "bg-ok-subtle text-ok", icon: "check_circle" },
  warn: { cls: "bg-warn-subtle text-warn", icon: "warning" },
  alarm: { cls: "bg-alarm-subtle text-alarm", icon: "error" },
  bad: { cls: "bg-bad-subtle text-bad", icon: "block" },
  info: { cls: "bg-info-subtle text-info", icon: "info" },
};
/** Icon colour on the floor variant, where the word itself stays ink for AAA (7:1) contrast. */
const FLOOR_ICON: Record<Tone, string> = {
  neutral: "text-ink-muted",
  ok: "text-ok",
  warn: "text-warn",
  alarm: "text-alarm",
  bad: "text-bad",
  info: "text-info",
};

/** Status is never colour alone: every chip carries an icon and a word. */
export function StatusChip({
  tone = "neutral",
  icon,
  floor,
  children,
}: {
  tone?: Tone;
  icon?: string;
  /** Shop-floor surfaces: ink word on white (AAA), the tone carried by the icon and the edge. */
  floor?: boolean;
  children: ReactNode;
}) {
  const t = TONE[tone];
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium leading-4",
        floor ? "border border-line-strong bg-surface text-ink" : t.cls,
      )}
    >
      <span className={floor ? FLOOR_ICON[tone] : undefined} aria-hidden="true">
        <Icon name={icon ?? t.icon} size={14} />
      </span>
      {children}
    </span>
  );
}
